/**
 * Maintenance Redirect Composable
 * Checks if maintenance mode is active for the current route and redirects accordingly
 */
import { useRouter, useRoute } from "vue-router";

// Define the structure for maintenance area pages
interface MaintenanceArea {
  area: string;
}

// Define the complete maintenance settings structure
interface MaintenanceSettings {
  documentId: string;
  enabled: boolean;
  startDate: string | null;
  endDate: string | null;
  maintenancePageSlug: string;
  pages: MaintenanceArea[];
}

// Map of area types to their corresponding routes
const AREA_ROUTE_MAP: Record<string, string> = {
  Dashboard: "/account/dashboard",
  Check_Out: "/checkout",
  Activation: "/sim-activation",
  Recharge: "/recharge",
  Plan_Change: "/account/dashboard?view=my-plans",
  Add_Ons: "/account/dashboard?view=extra-roaming",
};

export function useMaintenanceRedirect() {
  const mainStore = useMainStore();
  const router = useRouter();
  const route = useRoute();

  /**
   * Check if maintenance mode is active for the current route
   * and redirect to maintenance page if necessary
   */
  const checkMaintenanceAndRedirect = () => {
    const maintenance = mainStore.getMaintenance;

    // If no maintenance settings are available or maintenance is disabled, do nothing
    if (!maintenance || !maintenance.enabled) {
      return false;
    }

    // Cast to the correct type with the structure we expect
    const maintenanceSettings = maintenance as unknown as MaintenanceSettings;

    // Check if we're already on the maintenance page to avoid redirect loops
    if (route.path === `/${maintenanceSettings.maintenancePageSlug}`) {
      return false;
    }

    // Check date range if both startDate and endDate are specified
    if (maintenanceSettings.startDate && maintenanceSettings.endDate) {
      const now = new Date();
      const startDate = new Date(maintenanceSettings.startDate);
      const endDate = new Date(maintenanceSettings.endDate);

      // If current time is outside the maintenance window, do nothing
      if (now < startDate || now > endDate) {
        return false;
      }
    }

    // Check if Full_site is included in the maintenance areas
    const isFullSiteMaintenance = maintenanceSettings.pages.some(
      (page) => page.area === "Full_site"
    );

    // If Full_site is in maintenance, redirect to maintenance page
    if (isFullSiteMaintenance) {
      router.push(`/${maintenanceSettings.maintenancePageSlug}`);
      return true;
    }

    // Check if current route matches any area in maintenance
    const currentPath = route.path;
    let shouldRedirect = false;

    // Check each maintenance area against the current route
    for (const page of maintenanceSettings.pages) {
      const areaRoute = AREA_ROUTE_MAP[page.area];

      if (areaRoute) {
        // For routes with query parameters (like dashboard views)
        if (areaRoute.includes("?")) {
          const [basePath, queryPart] = areaRoute.split("?");
          const [queryKey, queryValue] = queryPart.split("=");

          // Check if base path matches and query parameter matches
          if (
            currentPath === basePath &&
            route.query[queryKey] === queryValue
          ) {
            shouldRedirect = true;
            break;
          }
        }
        // For simple path matching
        else if (currentPath === areaRoute) {
          shouldRedirect = true;
          break;
        }
      }
    }

    // If we should redirect based on the checks above
    if (shouldRedirect) {
      router.push(`/${maintenanceSettings.maintenancePageSlug}`);
      return true;
    }

    return false;
  };

  return {
    checkMaintenanceAndRedirect,
  };
}
